import type { LinkItemDto, NavItemDto } from '@/lib/sanity/dto';
import { META_NAV_VARIANT_DEFAULT, META_NAV_VARIANT_MAP } from '@/lib/ui/tailwind-mapping.constants';
import { stegaClean } from '@/lib/utils/stega';

export function hasLinkTarget(item?: LinkItemDto | null): boolean {
    if (!item) return false;

    if (item.linkType === 'external') {
        const href = item.href ? stegaClean(item.href).trim() : '';
        return !!href;
    }

    const slug = item.page?.slug ? stegaClean(item.page.slug).trim() : '';
    const anchor = item.anchor ? stegaClean(item.anchor).trim() : '';

    return !!(slug || anchor);
}

export function resolveHref(item: LinkItemDto, queryParams?: Record<string, string>): string {
    if (item.linkType === 'external') {
        const href = item.href ? stegaClean(item.href).trim() : '';
        return href ?? '#';
    }

    const pageSlug = item.page?.slug ? stegaClean(item.page.slug).trim() : '';
    let base = pageSlug ? `/${pageSlug}` : '/';

    if (item.openInModal) {
        base = `/m${base}`;
    }

    if (queryParams && Object.keys(queryParams).length > 0) {
        const searchParams = new URLSearchParams();
        Object.entries(queryParams).forEach(([key, value]) => {
            if (value) searchParams.append(key, value);
        });
        const queryString = searchParams.toString();
        if (queryString) {
            const separator = base.includes('?') ? '&' : '?';
            base = `${base}${separator}${queryString}`;
        }
    }

    const anchor = item.anchor ? stegaClean(item.anchor).trim() : '';
    if (anchor) {
        return `${base}#${anchor}`;
    }

    return base;
}

export function getAnchorId(href: string): string | null {
    const i = href.indexOf('#');
    return i === -1 ? null : href.slice(i + 1) || null;
}

export function getAnchorIds(items: LinkItemDto[]): string[] {
    const ids = items.map((item) => getAnchorId(resolveHref(item))).filter((id): id is string => Boolean(id));

    return Array.from(new Set(ids));
}

/**
 * Resolves the classes for a meta-navigation item from its Studio `variant`.
 * Unset or unknown values fall back to the default link style.
 */
export function resolveMetaNavClassName(item: NavItemDto): string {
    const variant = stegaClean(item.variant) ?? META_NAV_VARIANT_DEFAULT;

    return META_NAV_VARIANT_MAP[variant] ?? META_NAV_VARIANT_MAP[META_NAV_VARIANT_DEFAULT];
}
